import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThumbsUp, ThumbsDown, Search, ChevronLeft, ChevronRight, Plus, Users, DollarSign, Tag, Heart, Upload, X as XIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";

interface FundraisingIdea {
  id: string;
  title: string;
  description: string;
  detailed_plan: string;
  season: string[];
  location: string[];
  type: string[];
  estimated_helpers: number;
  estimated_income: string;
  estimated_pricing: string;
  likes: number;
  dislikes: number;
  image_url: string | null;
  status: string;
}

interface UserVote {
  idea_id: string;
  vote_type: 'like' | 'dislike';
}

export default function Library() {
  const { user } = useAuth();
  const [ideas, setIdeas] = useState<FundraisingIdea[]>([]);
  const [userVotes, setUserVotes] = useState<UserVote[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [seasonFilter, setSeasonFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [selectedIdea, setSelectedIdea] = useState<FundraisingIdea | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    detailed_plan: "",
    season: [] as string[],
    location: [] as string[],
    type: [] as string[],
    estimated_helpers: 0,
    estimated_income: "£0",
    estimated_pricing: "£0"
  });

  const itemsPerPage = 12;

  useEffect(() => {
    fetchIdeas();
    if (user) {
      fetchUserVotes();
      fetchFavorites();
    }
  }, [user]);

  const fetchIdeas = async () => {
    const { data, error } = await supabase
      .from('fundraising_ideas')
      .select('*')
      .eq('status', 'approved')
      .order('likes', { ascending: false });
    
    if (error) {
      toast({ title: "Error fetching ideas", description: error.message, variant: "destructive" });
    } else {
      setIdeas(data || []);
    }
  };

  const fetchUserVotes = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('user_idea_votes')
      .select('idea_id, vote_type')
      .eq('user_id', user.id);
    
    if (!error && data) {
      setUserVotes(data as UserVote[]);
    }
  };

  const fetchFavorites = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('user_favorites')
      .select('idea_id')
      .eq('user_id', user.id);
    
    if (!error && data) {
      setFavorites(data.map(f => f.idea_id));
    }
  };

  const handleVote = async (ideaId: string, voteType: 'like' | 'dislike') => {
    if (!user) {
      toast({ title: "Please sign in", description: "You need to be signed in to vote", variant: "destructive" });
      return;
    }

    const existingVote = userVotes.find(v => v.idea_id === ideaId);
    
    if (existingVote) {
      if (existingVote.vote_type === voteType) {
        // Remove vote
        await supabase
          .from('user_idea_votes')
          .delete()
          .eq('user_id', user.id)
          .eq('idea_id', ideaId);
        
        // Update idea count
        const idea = ideas.find(i => i.id === ideaId);
        if (idea) {
          await supabase
            .from('fundraising_ideas')
            .update({
              [voteType === 'like' ? 'likes' : 'dislikes']: Math.max(0, idea[voteType === 'like' ? 'likes' : 'dislikes'] - 1)
            })
            .eq('id', ideaId);
        }
      } else {
        // Change vote
        await supabase
          .from('user_idea_votes')
          .update({ vote_type: voteType })
          .eq('user_id', user.id)
          .eq('idea_id', ideaId);
        
        // Update idea counts
        const idea = ideas.find(i => i.id === ideaId);
        if (idea) {
          await supabase
            .from('fundraising_ideas')
            .update({
              likes: voteType === 'like' ? idea.likes + 1 : Math.max(0, idea.likes - 1),
              dislikes: voteType === 'dislike' ? idea.dislikes + 1 : Math.max(0, idea.dislikes - 1)
            })
            .eq('id', ideaId);
        }
      }
    } else {
      // New vote
      await supabase
        .from('user_idea_votes')
        .insert({ user_id: user.id, idea_id: ideaId, vote_type: voteType });
      
      // Update idea count
      const idea = ideas.find(i => i.id === ideaId);
      if (idea) {
        await supabase
          .from('fundraising_ideas')
          .update({
            [voteType === 'like' ? 'likes' : 'dislikes']: idea[voteType === 'like' ? 'likes' : 'dislikes'] + 1
          })
          .eq('id', ideaId);
      }
    }

    fetchIdeas();
    fetchUserVotes();
  };

  const handleToggleFavorite = async (ideaId: string) => {
    if (!user) {
      toast({ title: "Please sign in", description: "You need to be signed in to save favorites", variant: "destructive" });
      return;
    }

    const isFavorite = favorites.includes(ideaId);

    if (isFavorite) {
      await supabase
        .from('user_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('idea_id', ideaId);
    } else {
      await supabase
        .from('user_favorites')
        .insert({ user_id: user.id, idea_id: ideaId });
    }

    fetchFavorites();
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Image must be under 10MB", variant: "destructive" });
      return;
    }

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast({ title: "Invalid file type", description: "Only JPG and PNG files are allowed", variant: "destructive" });
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitIdea = async () => {
    if (!user) {
      toast({ title: "Please sign in", description: "You need to be signed in to submit ideas", variant: "destructive" });
      return;
    }

    if (!formData.title || !formData.description || !formData.detailed_plan) {
      toast({ title: "Required fields", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    setLoading(true);

    let imageUrl = null;
    if (imageFile) {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('fundraising-images')
        .upload(fileName, imageFile);

      if (uploadError) {
        toast({ title: "Image upload failed", description: uploadError.message, variant: "destructive" });
        setLoading(false);
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('fundraising-images')
        .getPublicUrl(fileName);
      imageUrl = publicUrl;
    }

    const { error } = await supabase
      .from('fundraising_ideas')
      .insert({
        title: formData.title,
        description: formData.description,
        detailed_plan: formData.detailed_plan,
        season: formData.season,
        location: formData.location,
        type: formData.type,
        estimated_helpers: formData.estimated_helpers,
        estimated_income: formData.estimated_income,
        estimated_pricing: formData.estimated_pricing,
        image_url: imageUrl,
        submitted_by: user.id,
        status: 'pending'
      });

    setLoading(false);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Your idea has been submitted for review!" });
      setSubmitOpen(false);
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      detailed_plan: "",
      season: [],
      location: [],
      type: [],
      estimated_helpers: 0,
      estimated_income: "£0",
      estimated_pricing: "£0"
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const toggleSeason = (season: string) => {
    setFormData(prev => ({
      ...prev,
      season: prev.season.includes(season)
        ? prev.season.filter(s => s !== season)
        : [...prev.season, season]
    }));
  };

  const toggleLocation = (location: string) => {
    setFormData(prev => ({
      ...prev,
      location: prev.location.includes(location)
        ? prev.location.filter(l => l !== location)
        : [...prev.location, location]
    }));
  };

  const toggleType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      type: prev.type.includes(type)
        ? prev.type.filter(t => t !== type)
        : [...prev.type, type]
    }));
  };

  // Filter and sort ideas
  const filteredIdeas = ideas.filter(idea => {
    const matchesSearch = idea.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         idea.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || idea.type?.some(t => t.toLowerCase() === typeFilter.toLowerCase());
    const matchesSeason = seasonFilter === "all" || idea.season.some(s => s.toLowerCase() === seasonFilter.toLowerCase());
    const matchesLocation = locationFilter === "all" || idea.location.some(l => l.toLowerCase() === locationFilter.toLowerCase());
    const matchesFavorites = !showFavoritesOnly || favorites.includes(idea.id);
    return matchesSearch && matchesType && matchesSeason && matchesLocation && matchesFavorites;
  });

  const sortedIdeas = [...filteredIdeas].sort((a, b) => {
    return b.likes - a.likes;
  });

  const totalPages = Math.ceil(sortedIdeas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedIdeas = sortedIdeas.slice(startIndex, startIndex + itemsPerPage);

  const getUserVote = (ideaId: string) => {
    return userVotes.find(v => v.idea_id === ideaId);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Fundraising Library</h1>
          <p className="text-muted-foreground mt-2">
            Proven fundraising ideas for your PTA
          </p>
        </div>
        <Dialog open={submitOpen} onOpenChange={(open) => {
          setSubmitOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Submit Idea
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Submit Your Fundraising Idea</DialogTitle>
              <DialogDescription>Share your successful fundraising idea with the community</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Summer Fair"
                />
              </div>
              <div>
                <Label htmlFor="description">Short Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief overview of the fundraising idea"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="detailed_plan">Detailed Plan *</Label>
                <Textarea
                  id="detailed_plan"
                  value={formData.detailed_plan}
                  onChange={(e) => setFormData({ ...formData, detailed_plan: e.target.value })}
                  placeholder="Step-by-step guide on how to execute this idea"
                  rows={6}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Type (select all that apply)</Label>
                  <div className="space-y-2 mt-2">
                    {['Event', 'Stall'].map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`type-${type}`}
                          checked={formData.type.includes(type)}
                          onCheckedChange={() => toggleType(type)}
                        />
                        <label
                          htmlFor={`type-${type}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {type}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Season (select all that apply)</Label>
                  <div className="space-y-2 mt-2">
                    {['Spring', 'Summer', 'Autumn', 'Winter', 'All Year'].map((season) => (
                      <div key={season} className="flex items-center space-x-2">
                        <Checkbox
                          id={`season-${season}`}
                          checked={formData.season.includes(season)}
                          onCheckedChange={() => toggleSeason(season)}
                        />
                        <label
                          htmlFor={`season-${season}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {season}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Location (select all that apply)</Label>
                  <div className="space-y-2 mt-2">
                    {['Indoor', 'Outdoor', 'Online'].map((location) => (
                      <div key={location} className="flex items-center space-x-2">
                        <Checkbox
                          id={`location-${location}`}
                          checked={formData.location.includes(location)}
                          onCheckedChange={() => toggleLocation(location)}
                        />
                        <label
                          htmlFor={`location-${location}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {location}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="helpers">Estimated Helpers</Label>
                  <Input
                    id="helpers"
                    type="number"
                    value={formData.estimated_helpers || ''}
                    onChange={(e) => setFormData({ ...formData, estimated_helpers: e.target.value ? parseInt(e.target.value) : 0 })}
                    placeholder="e.g., 5"
                  />
                </div>
                <div>
                  <Label htmlFor="income">Estimated Income</Label>
                  <Input
                    id="income"
                    value={formData.estimated_income}
                    onChange={(e) => setFormData({ ...formData, estimated_income: e.target.value })}
                    placeholder="e.g., £500-£800"
                  />
                </div>
                <div>
                  <Label htmlFor="pricing">Estimated Pricing</Label>
                  <Input
                    id="pricing"
                    value={formData.estimated_pricing}
                    onChange={(e) => setFormData({ ...formData, estimated_pricing: e.target.value })}
                    placeholder="e.g., £5 per ticket"
                  />
                 </div>
               </div>
               <div>
                 <Label htmlFor="image">Image (Optional)</Label>
                 <div className="mt-2">
                   {!imagePreview ? (
                     <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                       <div className="flex flex-col items-center justify-center pt-5 pb-6">
                         <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                         <p className="text-sm text-muted-foreground">Click to upload image</p>
                         <p className="text-xs text-muted-foreground">JPG, PNG (Max 10MB)</p>
                       </div>
                       <input
                         id="image"
                         type="file"
                         className="hidden"
                         accept="image/jpeg,image/png"
                         onChange={handleImageSelect}
                       />
                     </label>
                   ) : (
                     <div className="relative">
                       <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                       <Button
                         type="button"
                         size="icon"
                         variant="destructive"
                         className="absolute top-2 right-2"
                         onClick={() => {
                           setImageFile(null);
                           setImagePreview(null);
                         }}
                       >
                         <XIcon className="h-4 w-4" />
                       </Button>
                     </div>
                   )}
                 </div>
               </div>
               <Button onClick={handleSubmitIdea} disabled={loading} className="w-full">
                 {loading ? "Submitting..." : "Submit Idea"}
               </Button>
             </div>
           </DialogContent>
         </Dialog>
       </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Fundraising Ideas</CardTitle>
          <CardDescription>Browse, filter, and save your favorite ideas</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert>
              <AlertDescription className="text-sm">
                <strong>Note:</strong> The income, pricing, and helpers shown are estimates and can vary based on location, PTA size, school size, and other factors.
              </AlertDescription>
            </Alert>
            
            <div className="flex items-center justify-between mb-4">
              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              >
                <Heart className={`h-4 w-4 mr-2 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                {showFavoritesOnly ? 'Show All' : 'Show Favorites'} ({favorites.length})
              </Button>
            </div>
            
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search fundraising ideas..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="stall">Stall</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                </SelectContent>
              </Select>
              <Select value={seasonFilter} onValueChange={setSeasonFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Season" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Seasons</SelectItem>
                  <SelectItem value="spring">Spring</SelectItem>
                  <SelectItem value="summer">Summer</SelectItem>
                  <SelectItem value="autumn">Autumn</SelectItem>
                  <SelectItem value="winter">Winter</SelectItem>
                  <SelectItem value="all year">All Year</SelectItem>
                </SelectContent>
              </Select>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  <SelectItem value="indoor">Indoor</SelectItem>
                  <SelectItem value="outdoor">Outdoor</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedIdeas.map((idea) => {
                const userVote = getUserVote(idea.id);
                const isFavorite = favorites.includes(idea.id);
                return (
                  <Card key={idea.id} className="shadow-card hover:shadow-elevated transition-shadow relative">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute top-4 right-4 z-10"
                      onClick={() => handleToggleFavorite(idea.id)}
                    >
                      <Heart className={`h-5 w-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
                    </Button>
                     <CardContent className="pt-6 flex flex-col h-full">
                      {idea.image_url && (
                        <div className="mb-4 aspect-[4/3] overflow-hidden rounded-lg">
                          <img 
                            src={idea.image_url} 
                            alt={idea.title}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                      <div className="flex-grow flex flex-col">
                        <h4 className="font-semibold text-lg mb-2 pr-8">{idea.title}</h4>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{idea.description}</p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {idea.season.map(s => (
                            <Badge key={s} variant="secondary">{s}</Badge>
                          ))}
                          {idea.location.map(l => (
                            <Badge key={l} variant="outline">{l}</Badge>
                          ))}
                        </div>
                        <div className="space-y-1.5 text-sm text-muted-foreground mb-3 mt-auto">
                          <div className="flex items-center gap-2">
                            <Users className="h-3.5 w-3.5" />
                            <span>{idea.estimated_helpers} helpers</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5" />
                            <span>Income: {idea.estimated_income}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Tag className="h-3.5 w-3.5" />
                            <span>Pricing: {idea.estimated_pricing}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3 mt-4">
                        <Button
                          variant="default"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            setSelectedIdea(idea);
                            setDetailsOpen(true);
                          }}
                        >
                          View Details
                        </Button>
                        <div className="flex items-center justify-end gap-3 pt-3 border-t border-border text-sm">
                          <span className="flex items-center gap-1">
                            <ThumbsUp 
                              className={`h-4 w-4 cursor-pointer transition-all ${
                                userVote?.vote_type === 'like' 
                                  ? 'text-green-600 dark:text-green-500 stroke-2' 
                                  : 'text-muted-foreground hover:text-green-600 dark:hover:text-green-500'
                              }`}
                              onClick={() => handleVote(idea.id, 'like')}
                              strokeWidth={userVote?.vote_type === 'like' ? 2.5 : 2}
                            />
                            <span className="text-muted-foreground">{idea.likes}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsDown 
                              className={`h-4 w-4 cursor-pointer transition-all ${
                                userVote?.vote_type === 'dislike' 
                                  ? 'text-red-600 dark:text-red-500 stroke-2' 
                                  : 'text-muted-foreground hover:text-red-600 dark:hover:text-red-500'
                              }`}
                              onClick={() => handleVote(idea.id, 'dislike')}
                              strokeWidth={userVote?.vote_type === 'dislike' ? 2.5 : 2}
                            />
                            <span className="text-muted-foreground">{idea.dislikes}</span>
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {displayedIdeas.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No ideas found matching your criteria</p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedIdeas.length)} of {sortedIdeas.length} ideas
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedIdea?.title}</DialogTitle>
            <DialogDescription>{selectedIdea?.description}</DialogDescription>
          </DialogHeader>
          {selectedIdea && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{selectedIdea.season}</Badge>
                <Badge variant="outline">{selectedIdea.location}</Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                <div className="text-center">
                  <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-sm font-medium">{selectedIdea.estimated_helpers} Helpers</p>
                </div>
                <div className="text-center">
                  <DollarSign className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-sm font-medium">{selectedIdea.estimated_income}</p>
                </div>
                <div className="text-center">
                  <Tag className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-sm font-medium">{selectedIdea.estimated_pricing}</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Detailed Plan</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedIdea.detailed_plan}</p>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <ThumbsUp className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">{selectedIdea.likes} likes</span>
                </div>
                <div className="flex items-center gap-2">
                  <ThumbsDown className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium">{selectedIdea.dislikes} dislikes</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}